import React, { useState, useEffect, useMemo } from "react";
import Nav from "./Navigation.js";

const PUZZLE_IMAGE_URL = "https://pub-cf9e8eda097946d689b15497650e2a50.r2.dev/tempSlider.png";
const BOARD_CELL = 88;
const PAL_CELL = 62;

// --- Solver logic ---
// Tiles are 1-9. One tile (missingTile) is removed from the board each game.
// Goal: all present tiles in ascending order with blank at the missing tile's position.
// e.g. missing=3 → goal [1,2,0,4,5,6,7,8,9]
// Each tile N has goal position N-1 regardless of which tile is missing.

function manhattanDist(tiles) {
  let dist = 0;
  for (let i = 0; i < 9; i++) {
    if (tiles[i] === 0) continue;
    const goal = tiles[i] - 1; // tile N belongs at index N-1
    dist += Math.abs(Math.floor(i / 3) - Math.floor(goal / 3))
          + Math.abs(i % 3 - goal % 3);
  }
  return dist;
}

function isSolvable(tiles) {
  const flat = tiles.filter(n => n !== 0);
  let inv = 0;
  for (let i = 0; i < flat.length; i++)
    for (let j = i + 1; j < flat.length; j++)
      if (flat[i] > flat[j]) inv++;
  return inv % 2 === 0;
}

function buildGoal(missingTile) {
  const g = [];
  for (let i = 1; i <= 9; i++) g.push(i === missingTile ? 0 : i);
  return g;
}

function solveSlidingPuzzle(startTiles, missingTile) {
  const goal = buildGoal(missingTile);
  const goalStr = goal.join('');
  const startStr = startTiles.join('');
  if (startStr === goalStr) return [];
  if (!isSolvable(startTiles)) return null;

  const open = [{
    str: startStr, tiles: startTiles,
    g: 0, h: manhattanDist(startTiles), moves: [],
  }];
  const gScore = { [startStr]: 0 };

  while (open.length > 0) {
    let minI = 0;
    for (let i = 1; i < open.length; i++) {
      if (open[i].g + open[i].h < open[minI].g + open[minI].h) minI = i;
    }
    const { str, tiles, g, moves } = open.splice(minI, 1)[0];
    if (str === goalStr) return moves;

    const blankIdx = tiles.indexOf(0);
    const r = Math.floor(blankIdx / 3);
    const c = blankIdx % 3;

    const dirs = [];
    if (r > 0) dirs.push({ adj: blankIdx - 3, dir: 'down' });
    if (r < 2) dirs.push({ adj: blankIdx + 3, dir: 'up' });
    if (c > 0) dirs.push({ adj: blankIdx - 1, dir: 'right' });
    if (c < 2) dirs.push({ adj: blankIdx + 1, dir: 'left' });

    for (const { adj, dir } of dirs) {
      const next = [...tiles];
      const tileNum = next[adj];
      [next[blankIdx], next[adj]] = [next[adj], next[blankIdx]];
      const nextStr = next.join('');
      const newG = g + 1;
      if (gScore[nextStr] === undefined || newG < gScore[nextStr]) {
        gScore[nextStr] = newG;
        open.push({
          str: nextStr, tiles: next, g: newG,
          h: manhattanDist(next),
          moves: [...moves, { tileNum, dir, blankFrom: blankIdx, blankTo: adj }],
        });
      }
    }
  }
  return null;
}

function applyMoves(initialTiles, moves, count) {
  let t = [...initialTiles];
  for (let i = 0; i < count; i++) {
    const m = moves[i];
    [t[m.blankFrom], t[m.blankTo]] = [t[m.blankTo], t[m.blankFrom]];
  }
  return t;
}

// --- Tile rendering ---
// tileNum 1-9 → image section; 0 → blank (shown during solution playback)

function Tile({ tileNum, size, style = {}, ...rest }) {
  if (!tileNum) {
    return (
      <div style={{ width: size, height: size, backgroundColor: '#111827', borderRadius: 4, ...style }} {...rest} />
    );
  }
  const tileRow = Math.floor((tileNum - 1) / 3);
  const tileCol = (tileNum - 1) % 3;
  return (
    <div
      style={{
        width: size, height: size,
        backgroundImage: `url(${PUZZLE_IMAGE_URL})`,
        backgroundSize: `${3 * size}px ${3 * size}px`,
        backgroundPosition: `${-tileCol * size}px ${-tileRow * size}px`,
        borderRadius: 4,
        ...style,
      }}
      {...rest}
    />
  );
}

const DIR_ARROW = { up: '↑', down: '↓', left: '←', right: '→' };
const ALL_TILES = [1, 2, 3, 4, 5, 6, 7, 8, 9];

export default function SliderSolverPage({ isModal = false, onClose } = {}) {
  // board[i] = tileNum (1-9) or null = empty slot (= blank position)
  const [board, setBoard] = useState(Array(9).fill(null));
  // palette = image tiles not yet placed (starts with all 9)
  const [palette, setPalette] = useState([...ALL_TILES]);

  const [dragInfo, setDragInfo] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);

  const [initialTiles, setInitialTiles] = useState(null);
  const [solution, setSolution] = useState(null);
  const [solStep, setSolStep] = useState(0);
  const [showSol, setShowSol] = useState(false);
  const [error, setError] = useState(null);

  // Ready when 8 tiles placed: 1 slot empty (blank) + 1 tile remaining in palette (missing piece)
  const tilesPlaced = board.filter(t => t !== null).length;
  const readyToSolve = tilesPlaced === 8 && palette.length === 1;
  const missingTile = palette.length === 1 ? palette[0] : null;

  useEffect(() => {
    const onUp = () => {
      if (dragInfo) {
        const { source, index: fromIdx, tileNum } = dragInfo;
        const dt = dropTarget;

        if (dt && dt.target === 'board') {
          const toIdx = dt.index;
          const displaced = board[toIdx];

          if (source === 'palette') {
            setBoard(prev => { const n = [...prev]; n[toIdx] = tileNum; return n; });
            setPalette(prev => {
              let n = prev.filter(t => t !== tileNum);
              if (displaced !== null) n = [...n, displaced];
              return n.sort((a, b) => a - b);
            });
          } else if (source === 'board' && fromIdx !== toIdx) {
            setBoard(prev => {
              const n = [...prev];
              [n[fromIdx], n[toIdx]] = [n[toIdx], n[fromIdx]];
              return n;
            });
          }
        } else if (source === 'board') {
          setBoard(prev => { const n = [...prev]; n[fromIdx] = null; return n; });
          setPalette(prev => [...prev, tileNum].sort((a, b) => a - b));
        }
      }
      setDragInfo(null);
      setDropTarget(null);
    };
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchend', onUp);
    return () => { window.removeEventListener('mouseup', onUp); window.removeEventListener('touchend', onUp); };
  }, [dragInfo, dropTarget, board]);

  // Touch doesn't fire mouseenter/mouseleave as a finger moves over other
  // elements, so figure out the drop target manually from touch position.
  useEffect(() => {
    if (!dragInfo) return;
    const onTouchMove = (e) => {
      e.preventDefault();
      const touch = e.touches[0];
      if (!touch) return;
      const el = document.elementFromPoint(touch.clientX, touch.clientY);
      const boardEl = el?.closest('[data-board-index]');
      if (boardEl) {
        setDropTarget({ target: 'board', index: Number(boardEl.dataset.boardIndex) });
        return;
      }
      if (dragInfo.source === 'board' && el?.closest('[data-palette-drop]')) {
        setDropTarget({ target: 'palette', index: -1 });
        return;
      }
      setDropTarget(null);
    };
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    return () => window.removeEventListener('touchmove', onTouchMove);
  }, [dragInfo]);

  const handleSolve = () => {
    setError(null);
    const tiles = board.map(t => t === null ? 0 : t);
    if (!isSolvable(tiles)) {
      setError("This arrangement isn't solvable — check that you matched the game correctly.");
      return;
    }
    const sol = solveSlidingPuzzle(tiles, missingTile);
    if (!sol) { setError("No solution found."); return; }
    if (sol.length === 0) { setError("Already solved!"); return; }
    setInitialTiles([...tiles]);
    setSolution(sol);
    setSolStep(0);
    setShowSol(true);
  };

  const handleReset = () => {
    setBoard(Array(9).fill(null));
    setPalette([...ALL_TILES]);
    setSolution(null);
    setShowSol(false);
    setError(null);
    setInitialTiles(null);
  };

  const displayTiles = useMemo(() => {
    if (!showSol || !solution || !initialTiles) return board;
    return applyMoves(initialTiles, solution, solStep);
  }, [showSol, solution, solStep, initialTiles, board]);

  return (
    <div className="text-white">
      {!isModal && <Nav />}
      <div className={isModal ? "px-2 py-1" : "container mx-auto my-8 text-center"}>
        <div className="flex items-center justify-between mb-1">
          <h1 className="text-3xl font-bold flex-1 text-center">Slider Puzzle Solver</h1>
          {isModal && onClose && (
            <button onClick={onClose} className="text-gray-400 hover:text-white text-2xl leading-none ml-2" aria-label="Close">✕</button>
          )}
        </div>
        <p className="text-gray-400 mb-6 text-sm max-w-xl mx-auto">
          Drag tiles from the palette into the grid to match your in-game layout.
          Leave the <strong>one tile</strong> that isn't on the board in the palette — that's the missing piece.
          The empty slot you leave on the grid is the blank spot.
        </p>

        <div className="flex flex-col items-center gap-6 lg:flex-row lg:items-start lg:justify-center">
          <div className="flex flex-col items-center gap-4">

            {/* Board */}
            <div className="bg-gray-800 rounded-xl p-5 shadow-lg">
              <div className="flex justify-between items-center mb-3">
                <p className="text-sm text-gray-400">
                  {showSol
                    ? `Step ${solStep} / ${solution.length}`
                    : tilesPlaced === 0
                    ? 'Drag 8 tiles into position'
                    : readyToSolve
                    ? `Missing: tile ${missingTile} — Ready!`
                    : `${8 - tilesPlaced} tile${8 - tilesPlaced !== 1 ? 's' : ''} remaining`}
                </p>
                <div className="flex gap-3">
                  {showSol && (
                    <button onClick={() => { setShowSol(false); setSolution(null); }} className="text-xs text-gray-400 hover:text-white transition-colors">Edit</button>
                  )}
                  <button onClick={handleReset} className="text-xs text-gray-400 hover:text-white transition-colors">Reset</button>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
                {displayTiles.map((tileNum, idx) => {
                  const isEmpty = tileNum === null || tileNum === 0;
                  const isDropTarget = !showSol && dropTarget?.target === 'board' && dropTarget?.index === idx;

                  if (showSol) {
                    return <Tile key={idx} tileNum={tileNum || 0} size={BOARD_CELL} />;
                  }

                  if (isEmpty) {
                    return (
                      <div
                        key={idx}
                        data-board-index={idx}
                        onMouseEnter={() => setDropTarget({ target: 'board', index: idx })}
                        onMouseLeave={() => setDropTarget(null)}
                        style={{
                          width: BOARD_CELL, height: BOARD_CELL,
                          border: isDropTarget ? '2px solid white' : '2px dashed #374151',
                          borderRadius: 4,
                          backgroundColor: isDropTarget ? 'rgba(255,255,255,0.06)' : '#111827',
                          transition: 'border-color 0.1s, background-color 0.1s',
                        }}
                      />
                    );
                  }

                  const isDraggingThis = dragInfo?.source === 'board' && dragInfo?.index === idx;
                  return (
                    <Tile
                      key={idx}
                      tileNum={tileNum}
                      size={BOARD_CELL}
                      data-board-index={idx}
                      onMouseDown={() => setDragInfo({ source: 'board', index: idx, tileNum })}
                      onTouchStart={() => setDragInfo({ source: 'board', index: idx, tileNum })}
                      onMouseEnter={() => setDropTarget({ target: 'board', index: idx })}
                      onMouseLeave={() => setDropTarget(null)}
                      style={{
                        cursor: isDraggingThis ? 'grabbing' : 'grab',
                        opacity: isDraggingThis ? 0.35 : 1,
                        outline: isDropTarget && dragInfo ? '3px solid white' : 'none',
                        outlineOffset: '-3px',
                        userSelect: 'none',
                        touchAction: 'none',
                      }}
                    />
                  );
                })}
              </div>

              {error && (
                <p className="mt-3 text-red-400 text-sm bg-red-900 bg-opacity-30 rounded px-3 py-2">{error}</p>
              )}

              {!showSol && readyToSolve && (
                <button
                  onClick={handleSolve}
                  className="mt-4 w-full py-2 rounded bg-green-600 hover:bg-green-500 text-white text-sm font-semibold transition-colors"
                >
                  Solve
                </button>
              )}
            </div>

            {/* Palette — all 9 image sections */}
            {!showSol && (
              <div
                className="bg-gray-800 rounded-xl p-4"
                data-palette-drop="true"
                onMouseEnter={() => { if (dragInfo?.source === 'board') setDropTarget({ target: 'palette', index: -1 }); }}
                onMouseLeave={() => { if (dragInfo?.source === 'board') setDropTarget(null); }}
              >
                <p className="text-xs text-gray-400 mb-3 text-left">
                  Drag 8 tiles onto the grid — leave the missing piece here
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: `repeat(5, ${PAL_CELL}px)`, gap: 6 }}>
                  {palette.map(tileNum => {
                    const isDraggingThis = dragInfo?.source === 'palette' && dragInfo?.tileNum === tileNum;
                    return (
                      <Tile
                        key={tileNum}
                        tileNum={tileNum}
                        size={PAL_CELL}
                        onMouseDown={() => setDragInfo({ source: 'palette', index: -1, tileNum })}
                        onTouchStart={() => setDragInfo({ source: 'palette', index: -1, tileNum })}
                        style={{
                          cursor: isDraggingThis ? 'grabbing' : 'grab',
                          opacity: isDraggingThis ? 0.35 : 1,
                          userSelect: 'none',
                          touchAction: 'none',
                        }}
                      />
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Right: solution + instructions */}
          <div className="flex flex-col gap-3 w-56">
            {showSol && solution && (
              <div className="bg-gray-800 rounded-xl p-4 text-left">
                <p className="text-sm font-semibold mb-3">Solution ({solution.length} moves)</p>
                <ol className="text-sm space-y-1 mb-4 max-h-80 overflow-y-auto pr-1">
                  {solution.map((move, i) => (
                    <li
                      key={i}
                      onClick={() => setSolStep(i)}
                      className={`px-2 py-1 rounded cursor-pointer flex items-center gap-2 ${
                        i === solStep ? 'bg-blue-700 text-white'
                        : i < solStep ? 'text-gray-500'
                        : 'text-gray-300 hover:bg-gray-700'
                      }`}
                    >
                      <span className="font-medium w-5 shrink-0">{i + 1}.</span>
                      <Tile tileNum={move.tileNum} size={22} style={{ borderRadius: 2, flexShrink: 0 }} />
                      <span>{DIR_ARROW[move.dir]}</span>
                    </li>
                  ))}
                  <li
                    onClick={() => setSolStep(solution.length)}
                    className={`px-2 py-1 rounded cursor-pointer text-green-400 font-medium ${solStep === solution.length ? 'bg-green-900 bg-opacity-40' : 'hover:bg-gray-700'}`}
                  >
                    ✓ Solved!
                  </li>
                </ol>
                <div className="flex gap-2">
                  <button onClick={() => setSolStep(s => Math.max(0, s - 1))} disabled={solStep === 0} className="flex-1 py-1 text-xs rounded bg-gray-700 hover:bg-gray-600 disabled:opacity-40 transition-colors">← Prev</button>
                  <button onClick={() => setSolStep(s => Math.min(solution.length, s + 1))} disabled={solStep === solution.length} className="flex-1 py-1 text-xs rounded bg-gray-700 hover:bg-gray-600 disabled:opacity-40 transition-colors">Next →</button>
                </div>
              </div>
            )}

            <div className="bg-gray-800 rounded-xl p-3 text-xs text-gray-400 text-left">
              <p className="font-semibold text-gray-300 mb-1">How to use</p>
              <ul className="space-y-1 list-disc list-inside">
                <li>The palette shows all 9 picture sections</li>
                <li>Drag 8 tiles onto the grid to match your game</li>
                <li>Leave the 1 tile that's missing from your game in the palette</li>
                <li>Leave the empty slot where the blank is in your game</li>
                <li>Once 8 tiles are placed, hit Solve</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
